import React from 'react';

export const dailyStartButtonClassName =
  'inline-flex h-gameplay-control min-h-gameplay-control min-w-daily-start-button min-w-gameplay-control cursor-pointer touch-manipulation items-center justify-center gap-2 rounded-full border border-glyph-primary bg-glyph-primary px-6 text-gameplay-supporting-copy leading-none font-bold tracking-wide text-glyph-inverted shadow-default transition-all duration-fast ease-smooth hover:shadow-default-hover hover:brightness-110 disabled:cursor-default disabled:border-glyph-primary/60 disabled:bg-glyph-primary/60 disabled:text-glyph-inverted/60';

/** Public Daily content can paint before identity, streak and question data resolve. */
export function DailyChallengeFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="gameplay-scope mx-auto flex max-w-daily-start flex-col items-center gap-gameplay-section-gap px-4 py-gameplay-daily-screen-padding-y sm:gap-gameplay-section-gap sm:py-gameplay-daily-screen-padding-y">
      <div className="flex flex-col items-center">
        <h1 className="m-0 text-center font-body leading-tight font-bold tracking-daily-title text-glyph-primary">
          Daily Challenge
        </h1>
      </div>
      {children}
    </div>
  );
}

/** Loading presentation owns no player data, callbacks or game-run lifecycle. */
export function DailyChallengeLoadingScreen() {
  return (
    <div data-skeleton="true" data-skeleton-mode="daily" aria-busy="true">
      <DailyChallengeFrame>
        <div
          className="skeleton-shimmer min-h-52 min-w-72 rounded-2xl sm:min-h-48 sm:min-w-60"
          aria-hidden="true"
        />
        <button
          type="button"
          className={dailyStartButtonClassName}
          aria-label="Start daily challenge"
          disabled
        >
          Start
        </button>
      </DailyChallengeFrame>
    </div>
  );
}

interface GameSkeletonProps {
  mode?: 'endless' | 'daily' | 'quiz';
  showGenerationTag?: boolean;
  showSponsorFooter?: boolean;
  showSponsorLogo?: boolean;
  /** Keeps first-frame loading geometry/palette invariant while presentation resolves. */
  stablePresentation?: boolean;
}

const GameSkeleton: React.FC<GameSkeletonProps> = ({
  mode = 'endless',
  showGenerationTag,
  showSponsorFooter = false,
  showSponsorLogo = false,
  stablePresentation = false,
}) => {
  const shouldShowGenerationTag = showGenerationTag ?? mode !== 'quiz';

  return (
    <div
      className="gameplay-scope mx-auto w-full max-w-container"
      data-skeleton="true"
      data-skeleton-mode={mode}
      data-skeleton-stable={stablePresentation ? 'true' : undefined}
    >
      <div className="pb-gameplay-section-gap">
        <div>
          {shouldShowGenerationTag && (
            <div
              className="mb-gameplay-section-gap flex items-center gap-1"
              data-skeleton-part="breadcrumbs"
              aria-hidden="true"
            >
              <div className="skeleton-shimmer h-control-lg w-control-lg min-w-control-lg rounded-full" />
              <span className="mx-0.5 flex flex-shrink-0 items-center justify-center text-glyph-secondary/40">
                ›
              </span>
              <div className="skeleton-shimmer h-control-lg min-w-32 rounded-full" />
            </div>
          )}
          {mode === 'quiz' && (
            <div className="mb-gameplay-section-gap -ml-1 flex items-center" aria-hidden="true">
              <div
                className="skeleton-shimmer h-6 w-28 rounded-full"
                data-skeleton-part="quiz-progress"
              />
            </div>
          )}
          <div
            className="skeleton-shimmer mb-3 w-full rounded-md"
            style={{
              height: 'calc(var(--gameplay-font-size-question-heading) * 2.2)',
              lineHeight: 'clamp(0.95em, 5vw + 0.3em, 1.2em)',
            }}
            aria-hidden="true"
          />
        </div>
      </div>

      <div data-dc={mode === 'daily' ? '1' : '0'}>
        <div className="mb-gameplay-section-gap w-full">
          <div
            data-skeleton-part="answer-grid"
            className="-ml-0.5 grid grid-cols-1 gap-0 overflow-hidden rounded-lg border border-outline-subtle/30 lg:grid-cols-2 lg:gap-px"
          >
            <button
              className="skeleton-shimmer pointer-events-none min-h-gameplay-answer-row w-full rounded-none text-left text-transparent"
              style={{
                paddingBlock: 'calc((48px - 1.4em) / 2)',
              }}
              data-skeleton-part="answer"
              aria-hidden="true"
            />
            <button
              className="skeleton-shimmer pointer-events-none min-h-gameplay-answer-row w-full rounded-none text-left text-transparent"
              style={{
                paddingBlock: 'calc((48px - 1.4em) / 2)',
              }}
              data-skeleton-part="answer"
              aria-hidden="true"
            />
            <button
              className="skeleton-shimmer pointer-events-none min-h-gameplay-answer-row w-full rounded-none text-left text-transparent"
              style={{
                paddingBlock: 'calc((48px - 1.4em) / 2)',
              }}
              data-skeleton-part="answer"
              aria-hidden="true"
            />
            <button
              className="skeleton-shimmer pointer-events-none min-h-gameplay-answer-row w-full rounded-none text-left text-transparent"
              style={{
                paddingBlock: 'calc((48px - 1.4em) / 2)',
              }}
              data-skeleton-part="answer"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      {mode === 'daily' ? (
        <div
          className="flex min-h-gameplay-daily-metrics w-full items-center justify-end"
          data-skeleton-part="daily-metrics"
          aria-hidden="true"
        >
          <div className="flex gap-gameplay-daily-metrics-gap">
            {Array.from({ length: 7 }, (_, index) => (
              <div
                key={`daily-metric-skeleton-${index}`}
                className="skeleton-shimmer h-4 w-4 rounded-full"
                data-skeleton-part="metric-indicator"
              />
            ))}
          </div>
        </div>
      ) : mode === 'endless' ? (
        <div
          className="flex min-h-gameplay-metric-pill w-full items-center justify-end py-1.5"
          data-skeleton-part="endless-metrics"
          aria-hidden="true"
        >
          <div className="flex items-center gap-1.5">
            {Array.from({ length: 3 }, (_, index) => (
              <div
                key={`endless-life-skeleton-${index}`}
                className="skeleton-shimmer h-7 w-7 rounded-full"
                data-skeleton-part="metric-indicator"
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-3" data-skeleton-part="quiz-progress" aria-hidden="true" />
      )}

      {showSponsorFooter && (
        <div
          className="mt-gameplay-section-gap flex flex-shrink-0 items-center justify-center gap-2 py-2"
          data-skeleton-part="sponsor-footer"
          aria-hidden="true"
        >
          {showSponsorLogo && (
            <div
              className="skeleton-shimmer h-5 w-10 shrink-0 rounded"
              data-skeleton-part="sponsor-logo"
            />
          )}
          <div className="skeleton-shimmer h-3 w-40 rounded" />
        </div>
      )}
    </div>
  );
};

export default GameSkeleton;
